import { nanoid } from "nanoid"
import Logger from "@/utils/Logger"

export interface SsrJobLink {
  url: string
  title: string
}

// Number of offers linked per surface. Fixed at Typesense's max hits per page:
// one request, no pagination. Jobshops with more offers are not fully linked
// here — the XML sitemap (sitemap-jobs.xml) remains the complete URL source.
const LINK_LIMIT = 250

/**
 * Dependencies resolved by the caller *before the first `await`* in its
 * `async setup()` and passed in here. This composable therefore never calls
 * `getCurrentInstance()`-dependent hooks (`useNuxtApp`, `useHeaderStore`,
 * `useRuntimeConfig`) itself — doing so after an await in the caller's setup
 * silently breaks (see the AGENTS.md async-setup constraint).
 */
export interface SsrJobLinksContext {
  api: ReturnType<typeof useNuxtApp>["$api"]
  tenantId: string
  jobShopId: string
  typesenseUrl: string
}

interface UseSsrJobLinksOptions {
  // Scope suffix used to keep the useAsyncData cache key unique per surface
  // (e.g. "search" for the search page, the module id for a content module).
  scope: string
  // Query preset — mirrors the module's `search.preset` (empty on the search page).
  query?: string
  // Typesense `filter_by` string — the module's active preset filters so the
  // crawler links match the offers the module actually shows (see resolvePresetFilters).
  filters?: string
}

/**
 * Fetches up to LINK_LIMIT offers a search surface displays and exposes them as
 * plain { url, title } links, rendered into a <noscript> block (see
 * SsrJobLinks.vue) so job links are present in the initial HTML for crawlers
 * that do not execute JavaScript — the search UI itself is client-only
 * (Typesense InstantSearch).
 *
 * Uses a lean direct Typesense query (`api.typesense.getOffers`) that returns
 * only `url` + `title` and skips semantic/vector ranking, so it stays cheap. The
 * module passes its preset `filters` so the links match the offer subset it
 * renders; the link URL is the offer's own `url` field — the same value the
 * visible ResultItem links to. Completeness beyond LINK_LIMIT is covered by the
 * XML sitemap, not this list. (JET-1826)
 */
export async function useSsrJobLinks(typesenseApiKey: string | null | undefined, context: SsrJobLinksContext, options: UseSsrJobLinksOptions) {
  const { api, tenantId, jobShopId, typesenseUrl } = context

  const requestId = nanoid()
  const contextString = `SSR job links for jobShopId: "${jobShopId}", scope: "${options.scope}"`

  if (!typesenseApiKey) {
    if (import.meta.server)
      Logger(requestId).error(`Skipping ${contextString}: Typesense API key is missing`)

    return { jobLinks: computed<SsrJobLink[]>(() => []) }
  }

  let startTime
  if (import.meta.server) {
    Logger(requestId).start(`Start fetching ${contextString}`)
    startTime = Date.now()
  }

  const { data, error } = await useAsyncData<SsrJobLink[]>(`ssrJobLinks-${jobShopId}-${options.scope}`, async () => {
    // Only fetch during SSR: the <noscript> list exists solely for non-JS
    // crawlers, which always receive freshly server-rendered HTML. Running on the
    // client would fire a Typesense request on every SPA navigation to build
    // markup no JS user ever sees.
    if (import.meta.client)
      return []

    // Server-only, so no 401 retry (see withTypesenseKeyRetry).
    const response = await api.typesense.getOffers(tenantId, jobShopId, typesenseApiKey, typesenseUrl, {
      query: options.query,
      filterBy: options.filters,
      perPage: LINK_LIMIT,
    })

    return (response?.results?.[0]?.hits ?? [])
      .map(hit => ({ url: hit.document?.url as string, title: hit.document?.title as string }))
      .filter((link: SsrJobLink) => !!link.url && !!link.title)
  })

  if (import.meta.server) {
    if (!error.value) {
      const duration = Date.now() - (startTime ?? 0)
      Logger(requestId).success(`Successfully fetched ${contextString}: ${data.value?.length ?? 0} links. (${duration}ms)`)
    }
    else {
      Logger(requestId).error(`Error while fetching ${contextString}: ${error.value?.message ?? "Unknown error"}\n\n`, error.value)
    }
  }

  return { jobLinks: computed<SsrJobLink[]>(() => data.value ?? []) }
}
